/**
 * Where the visitor is, for the Multiconexão and Multi Internet simulations.
 *
 * GEO_ENDPOINT is the ONE place for the site's geolocation endpoint (GET, JSON:
 * `{ country, region, city, lat?, lon?, isp? }`; alternate key names are accepted). Empty means no request:
 * both cards use the fallback profile (United States). QA: `?mcgeo=BR|MG|Belo Horizonte&mcisp=Vivo` on the
 * page URL forces a location (and, for Multi Internet, the visitor's provider).
 */
export const GEO_ENDPOINT = '';
export const GEO_TIMEOUT = 2500;

/** [name, 3-letter code, lat, lon] */
export type CityTuple = [string, string, number, number];

export interface GeoData {
  /** cc -> [country name (pt-BR), hub city, cities] */
  countries: Record<string, [string, string, CityTuple[]]>;
  /** cc -> region code -> city */
  regions: Record<string, Record<string, string>>;
  /** folded foreign spelling -> the dataset's (pt-BR) spelling */
  aliases: Record<string, string>;
  /** cc -> { fixed: [brand, kind][], mobile: [brand, has 5G][] } */
  providers: Record<string, { fixed?: [string, string][]; mobile?: [string, boolean][] }>;
}

export interface RawGeoAnswer {
  country?: string;
  region?: string;
  city?: string;
  lat?: string | number | null;
  lon?: string | number | null;
  isp?: string;
}

let geoDataPromise: Promise<GeoData> | null = null;

/** The dataset (53 KB) ships in its own chunk and is loaded once, when the first card mounts. */
export function loadGeoData(): Promise<GeoData> {
  if (!geoDataPromise) {
    geoDataPromise = import('../data/geo-data.json').then((module) => (module.default ?? module) as unknown as GeoData);
  }
  return geoDataPromise;
}

let geoAnswerPromise: Promise<unknown> | null = null;

/** One request per page: both cards share it. Resolves null on no endpoint, error or timeout. */
export function requestVisitorGeo(): Promise<unknown> {
  if (!GEO_ENDPOINT || typeof fetch !== 'function') return Promise.resolve(null);
  if (!geoAnswerPromise) {
    geoAnswerPromise = new Promise((resolve) => {
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = window.setTimeout(() => {
        controller?.abort();
        resolve(null);
      }, GEO_TIMEOUT);
      fetch(GEO_ENDPOINT, { credentials: 'same-origin', signal: controller?.signal })
        .then((response) => (response.ok ? response.json() : null))
        .then(
          (data: unknown) => {
            window.clearTimeout(timer);
            resolve(data);
          },
          () => {
            window.clearTimeout(timer);
            resolve(null);
          }
        );
    });
  }
  return geoAnswerPromise;
}

/** `?mcgeo=CC|REGION|City|lat|lon` and `&mcisp=Brand`, decoded; null when the page has no `mcgeo`. */
export function forcedGeoParams(search: string): RawGeoAnswer | null {
  try {
    const geoMatch = /[?&]mcgeo=([^&#]*)/.exec(search);
    if (!geoMatch) return null;
    const ispMatch = /[?&]mcisp=([^&#]*)/.exec(search);
    const parts = decodeURIComponent((geoMatch[1] ?? '').replace(/\+/g, ' ')).split('|');
    return { country: parts[0], region: parts[1], city: parts[2], lat: parts[3], lon: parts[4], isp: ispMatch ? decodeURIComponent((ispMatch[1] ?? '').replace(/\+/g, ' ')) : '' };
  } catch {
    return null;
  }
}

export function foldText(text: unknown, keep = ''): string {
  const pattern = new RegExp(`[^a-z0-9${keep}]+`, 'g');
  return String(text ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(pattern, ' ')
    .trim();
}

export function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character] ?? character);
}

/**
 * City names in the dataset are pt-BR spellings ("Nova York", "Tóquio"). Every other locale gets the
 * English exonym the dataset's own alias table carries ("new york" -> "Nova York"), title-cased; a city
 * with no alias keeps its own name, which is already the local one ("São Paulo", "Chicago").
 */
export function createCityNamer(geo: GeoData, locale: string): (name: string) => string {
  if (locale === 'pt') return (name) => name;
  const english = new Map<string, string>();
  Object.entries(geo.aliases).forEach(([alias, datasetName]) => {
    const current = english.get(datasetName);
    if (!current || alias.length < current.length) english.set(datasetName, alias);
  });
  const titleCase = (text: string) => text.replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
  return (name) => {
    const alias = english.get(name);
    return alias ? titleCase(alias) : name;
  };
}

const LOCALE_TAGS: Record<string, string> = { pt: 'pt-BR', en: 'en', es: 'es', fr: 'fr', ru: 'ru', tr: 'tr', cn: 'zh-CN', jp: 'ja', kr: 'ko', sa: 'ar' };

/** BCP 47 tag for the site's locale slug (`cn` -> `zh-CN`, `sa` -> `ar`...). */
export function localeTag(locale: string): string {
  return LOCALE_TAGS[locale] ?? 'en';
}

/** Country names in the page's language (Intl), falling back to the dataset's pt-BR name. */
export function createCountryNamer(geo: GeoData, locale: string): (countryCode: string) => string {
  let displayNames: Intl.DisplayNames | null = null;
  try {
    displayNames = new Intl.DisplayNames([localeTag(locale)], { type: 'region' });
  } catch {
    displayNames = null;
  }
  return (countryCode) => {
    const fallback = geo.countries[countryCode]?.[0] ?? countryCode;
    if (locale === 'pt' || !displayNames) return fallback;
    try {
      return displayNames.of(countryCode) ?? fallback;
    } catch {
      return fallback;
    }
  };
}
